import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';

// Define the key democratic areas that can be impacted
const democraticAreas = [
  { id: 'free_elections', label: 'Free & Fair Elections', color: 'blue' },
  { id: 'rule_of_law', label: 'Rule of Law', color: 'indigo' },
  { id: 'free_speech', label: 'Free Speech & Press', color: 'purple' },
  { id: 'minority_rights', label: 'Minority Rights', color: 'pink' },
  { id: 'checks_balances', label: 'Checks & Balances', color: 'red' },
  { id: 'civic_participation', label: 'Civic Participation', color: 'orange' },
  { id: 'truth_facts', label: 'Truth & Facts', color: 'yellow' },
  { id: 'peaceful_transfer', label: 'Peaceful Transfer of Power', color: 'green' }
];

// Keywords that might indicate impacts on specific areas
const impactKeywords = {
  free_elections: ['election', 'vote', 'ballot', 'poll', 'voter', 'voting', 'electoral'],
  rule_of_law: ['law', 'justice', 'court', 'legal', 'judge', 'constitution', 'judicial'],
  free_speech: ['speech', 'press', 'media', 'journalist', 'news', 'expression', 'censorship'],
  minority_rights: ['minority', 'rights', 'discrimination', 'equal', 'equality', 'immigrant', 'diversity'],
  checks_balances: ['congress', 'executive', 'judicial', 'branch', 'power', 'authority', 'oversight'],
  civic_participation: ['civic', 'citizen', 'participate', 'protest', 'assembly', 'community', 'engagement'],
  truth_facts: ['truth', 'fact', 'accurate', 'misinformation', 'disinformation', 'lie', 'false'],
  peaceful_transfer: ['transition', 'transfer', 'power', 'peaceful', 'election', 'administration', 'inauguration']
};

const DemocraticImpactVisualizer = ({ claims = [] }) => {
  // Analyze the democratic impact of all claims
  const impactAnalysis = useMemo(() => {
    if (!claims || claims.length === 0) {
      return null;
    }
    
    // Initialize impact score for each area
    const impactScores = democraticAreas.reduce((acc, area) => {
      acc[area.id] = { score: 0, relevantClaims: [] };
      return acc;
    }, {});
    
    // Analyze each claim's impact
    claims.forEach(claim => {
      if (!claim.democratic_impact || !claim.importance_level) {
        return;
      }
      
      const impactText = claim.democratic_impact.toLowerCase();
      const importanceMultiplier = claim.importance_level / 3; // Normalize importance
      
      // Check if any keywords match
      democraticAreas.forEach(area => {
        const keywordsForArea = impactKeywords[area.id] || [];
        let hasMatch = false;
        
        // Check if any keywords for this area appear in the impact text
        keywordsForArea.forEach(keyword => {
          if (impactText.includes(keyword.toLowerCase())) {
            hasMatch = true;
          }
        });
        
        // If there's a match, add to the score
        if (hasMatch) {
          impactScores[area.id].score += importanceMultiplier;
          impactScores[area.id].relevantClaims.push(claim);
        }
      });
    });
    
    // Normalize scores to be out of 10
    const maxPossibleScore = claims.length * (5/3); // Max if all claims had max importance
    const normalizedScores = Object.keys(impactScores).reduce((acc, areaId) => {
      const rawScore = impactScores[areaId].score;
      // Scale score to be out of 10, min 0
      acc[areaId] = {
        ...impactScores[areaId],
        normalizedScore: Math.min(10, (rawScore / maxPossibleScore) * 10)
      };
      return acc;
    }, {});
    
    return normalizedScores;
  }, [claims]);
  
  // Sort democratic areas by impact score
  const sortedAreas = useMemo(() => {
    if (!impactAnalysis) {
      return democraticAreas;
    }
    
    return [...democraticAreas].sort((a, b) => {
      const scoreA = impactAnalysis[a.id]?.normalizedScore || 0;
      const scoreB = impactAnalysis[b.id]?.normalizedScore || 0;
      return scoreB - scoreA;
    });
  }, [impactAnalysis]);
  
  // Return null if no claims
  if (!claims || claims.length === 0) {
    return null;
  }
  
  // If no claims have democratic impact analysis
  const hasDemocraticImpact = claims.some(claim => claim.democratic_impact);
  if (!hasDemocraticImpact) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mt-4">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
          <span className="text-sm text-yellow-700">
            No democratic impact analysis available for these claims. Add democratic impact information to each claim to generate a visualization.
          </span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-md border border-gray-200 p-4 mt-4">
      <h3 className="text-lg font-medium text-gray-900 mb-2">Democratic Impact Assessment</h3>
      <p className="text-sm text-gray-500 mb-4">
        Based on the claims analyzed, here are the areas of democracy potentially impacted:
      </p>
      
      <div className="space-y-3">
        {sortedAreas.map(area => {
          const areaData = impactAnalysis[area.id];
          const score = areaData?.normalizedScore || 0;
          const claims = areaData?.relevantClaims || [];
          
          // Skip areas with no impact
          if (score === 0) {
            return null;
          }
          
          // Map score to severity color
          let severityColor;
          if (score < 3) {
            severityColor = 'bg-green-500';
          } else if (score < 6) {
            severityColor = 'bg-yellow-500';
          } else {
            severityColor = 'bg-red-500';
          }
          
          return (
            <div key={area.id} className="border border-gray-200 rounded-md p-3">
              <div className="flex justify-between items-center mb-1">
                <span className="font-medium text-sm">{area.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  score < 3 ? 'bg-green-100 text-green-800' :
                  score < 6 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  Impact: {score.toFixed(1)}/10
                </span>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className={`${severityColor} h-2 rounded-full`}
                  style={{ width: `${score * 10}%` }}
                />
              </div>
              
              {claims.length > 0 && (
                <div className="text-xs text-gray-500">
                  Based on {claims.length} claim{claims.length > 1 ? 's' : ''}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DemocraticImpactVisualizer;